import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router"
import { Alert, Box, Button, Divider, Grid, IconButton, Paper, Stack, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { Bound } from "lib/@components/binding/Bound"
import { If, Else } from "lib/@components/switch"
import { useRefresh } from "lib/@hooks/useRefresh"
import { Secure } from "lib/authorization/secure"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { AUTHORING_MANAGE_LEGISLATION_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { getAuthoringLegislation } from "./controller/get-authoring-legislation"
import { updateAuthoringLegislation } from "./controller/update-authoring-legislation"
import { LegislationDetailView } from "./components/legislation-detail-view"
import { LegislationDetailForm } from "./components/legislation-detail-form"
import { LegislationLinkHealthSection } from "./components/legislation-link-health-section"
import { LegislationDetailSidebar } from "./components/legislation-detail-sidebar"
import { useAuthoringLegislationLibrary } from "./use-authoring-legislation-library"
import { confirmLegislationUrlChange, confirmDuplicateDestination } from "./legislation-library-helpers"
import { isValidLegislationUrl } from "./server/lib/legislation-library-validation.mjs"

const EDITABLE_FIELDS = ["title", "url", "description"]

export function LegislationDetailPage() {
    const { id } = useParams()
    const legislationResult = getAuthoringLegislation.useResults({ id }, withInitialValue(IS_LOADING))
    const isLoading = legislationResult === IS_LOADING
    const legislation = isLoading ? null : legislationResult ?? null
    const schedules = legislation?.schedules ?? []
    const customScheduleCount = legislation?.customScheduleCount ?? 0
    const library = useAuthoringLegislationLibrary()

    const refresh = useRefresh()
    const [isEditing, setIsEditing] = useState(false)
    const [optimisticLegislation, setOptimisticLegislation] = useState(null)
    const [saveError, setSaveError] = useState(null)
    const [saving, setSaving] = useState(false)
    const draftRef = useRef(null)
    if (!draftRef.current && legislation) draftRef.current = cloneLegislation(legislation)

    // Resync only on a different record or a saved change; depending on `legislation` would drop an in-progress edit on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(syncDraft, [legislation?.id, legislation?.lastModified])

    const displayLegislation = optimisticLegislation ?? legislation
    const pageTitle = displayLegislation?.title ?? "Legislation"

    if (isLoading || !legislation) return <LoadingScreen />

    const draft = draftRef.current
    const saveDisabled = !draft?.title?.trim() || !isValidLegislationUrl(draft?.url)
    const editLabel = saving ? "Saving…" : "Edit"

    return (
        <PageBreadcrumbContainer
            title={pageTitle}
            hideCrumbs
            backButton={false}
            heading={
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ width: 1, minWidth: 0 }}
                >
                    <Stack direction="row">
                        <IconButton
                            onClick={handleBack}
                            disabled={saving}
                            size="small"
                            aria-label="Back"
                            data-cy="legislation-detail-back"
                            sx={{ color: "sfg20.medium", flexShrink: 0 }}
                        >
                            <Iconify icon="mdi:arrow-left" sx={{ width: 24, height: 24 }} />
                        </IconButton>
                        <TruncatedTypography
                            variant="h4"
                            data-cy="legislation-detail-title"
                            sx={{ flex: 1, minWidth: 0, maxWidth: "70%", fontWeight: 700 }}
                        >
                            {pageTitle}
                        </TruncatedTypography>
                    </Stack>
                    <Secure demand={AUTHORING_MANAGE_LEGISLATION_DEMAND} fallback={<></>}>
                        <If value={isEditing}>
                            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                                <Button variant="outlined" onClick={cancelEdit} data-cy="legislation-detail-cancel">
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={save}
                                    disabled={saveDisabled}
                                    data-cy="legislation-detail-save"
                                >
                                    Save Changes
                                </Button>
                            </Stack>
                            <Else>
                                <Button
                                    variant="outlined"
                                    onClick={enterEditMode}
                                    disabled={saving}
                                    startIcon={<Iconify icon="mdi:pencil" />}
                                    data-cy="legislation-detail-edit"
                                    sx={{ flexShrink: 0 }}
                                >
                                    {editLabel}
                                </Button>
                            </Else>
                        </If>
                    </Secure>
                </Stack>
            }
        >
            <Box sx={{ width: 1, flex: 1, minHeight: 0, mt: 2, display: "flex" }} data-cy="legislation-detail-page">
                <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <Iconify
                                    icon="mdi:scale-balance"
                                    sx={{ width: 22, height: 22, color: "sfg20.medium" }}
                                />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Legislation
                                </Typography>
                            </Stack>
                            <Divider sx={{ mb: 3 }} />

                            <If value={isEditing}>
                                <If value={!!saveError}>
                                    <Alert severity="error" sx={{ mb: 2 }} data-cy="legislation-detail-save-error">
                                        {saveError}
                                    </Alert>
                                </If>
                                <Bound target={draftRef.current} refresh={refresh} library={library}>
                                    <LegislationDetailForm id={id} />
                                </Bound>
                                <Else>
                                    <LegislationDetailView legislation={displayLegislation} />
                                </Else>
                            </If>

                            <LegislationLinkHealthSection key={legislation.redirectId} id={id} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ display: "flex", minHeight: 0, alignSelf: "flex-start" }}>
                        <Bound schedules={schedules} customScheduleCount={customScheduleCount}>
                            <LegislationDetailSidebar />
                        </Bound>
                    </Grid>
                </Grid>
            </Box>
        </PageBreadcrumbContainer>
    )

    function syncDraft() {
        if (legislation) draftRef.current = cloneLegislation(legislation)
        setOptimisticLegislation(null)
        setIsEditing(false)
        setSaveError(null)
    }

    function enterEditMode() {
        draftRef.current = cloneLegislation(legislation)
        setIsEditing(true)
        setSaveError(null)
    }

    function cancelEdit() {
        draftRef.current = cloneLegislation(legislation)
        setIsEditing(false)
        setSaveError(null)
    }

    function isDraftDirty() {
        if (!draftRef.current || !legislation) return false
        if (draftRef.current.folderIds !== undefined) return true
        return EDITABLE_FIELDS.some((f) => (draftRef.current[f] ?? "") !== (legislation[f] ?? ""))
    }

    async function handleBack() {
        if (isEditing && isDraftDirty()) {
            const confirmed = await confirm(
                "You have unsaved changes. Are you sure you want to leave?",
                "Discard changes?"
            )
            if (!confirmed) return
        }
        window.history.back()
    }

    async function save() {
        const draft = draftRef.current
        if (!draft) return
        const confirmed = await confirmLegislationUrlChange({
            scheduleCount: legislation?.scheduleCount,
            previousUrl: legislation?.url,
            nextUrl: draft.url,
        })
        if (!confirmed) return

        if (draft.url !== legislation?.url && !(await confirmDuplicateDestination(draft.url, id))) return

        const { title, url, description, folderIds } = draft
        setSaveError(null)
        setSaving(true)
        // Show the edited values straight away; reopen the editor only if the save fails.
        setOptimisticLegislation({ ...legislation, title, url, description })
        setIsEditing(false)

        try {
            await updateAuthoringLegislation({ id, legislation: { title, url, description, folderIds } })
            successSnackbar("Legislation updated")
        } catch (e) {
            console.error(e)
            setOptimisticLegislation(null)
            setSaveError(e?.message ?? "Failed to save changes")
            setIsEditing(true)
        } finally {
            setSaving(false)
        }
    }
}

function cloneLegislation(legislation) {
    return {
        title: legislation?.title ?? "",
        url: legislation?.url ?? "",
        description: legislation?.description ?? "",
    }
}
